import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView, type MenuAction } from '@/card/record'
import { Icon } from '@/components/Icon'
import type { Locale } from '@/i18n'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  RowMenu,
  SkeletonTable,
  isForbidden,
} from '@/admin/AdminShell'
import type { DepartmentProgress, EmployeeProgress, PlanInput, Quarter } from './plansApi'
import {
  PLANS_KEY,
  RESOURCE,
  createPlan,
  fetchAssignees,
  fetchBreakdown,
  fetchDepartmentProgress,
  fetchEmployeeProgress,
  fetchForecast,
  parseAmount,
  quarterOf,
  quarterRange,
  revisePlan,
  shiftQuarter,
} from './plansApi'

/**
 * Планы продаж (#119): «руководитель задаёт план на квартал и видит выполнение по каждому
 * менеджеру».
 *
 * Квартал выбирается сверху; ниже — план-факт по менеджерам и по подразделениям за него.
 * Кого видно, решает сервер областью `sales.plan:read` (руководитель — свой отдел, менеджер
 * — себя), а сделки под фактом — областью `sales.deal:read`. Экран это не повторяет.
 *
 * Чего экран не делает молча:
 * - нулевой план — прочерк «цель не задана», а не 0%: «не ставили» и «провалил» — разные
 *   разговоры;
 * - план есть, продаж нет — честные 0%, а не прочерк;
 * - отказ по правам — словами, а не пустой таблицей;
 * - прогноз — две цифры (заработано и взвешенная воронка), а не одна «прогнозная».
 */
export function SalesPlansPage() {
  const { t, locale, user } = useApp()
  const [quarter, setQuarter] = useState<Quarter>(() => quarterOf(new Date()))
  const range = quarterRange(quarter)
  const [creating, setCreating] = useState(false)
  const [opened, setOpened] = useState<Opened | null>(null)
  const [revising, setRevising] = useState<Opened | null>(null)
  const mayCreate = can(user, RESOURCE, 'create')
  const mayRevise = can(user, RESOURCE, 'update')

  const employees = useQuery({
    queryKey: [...PLANS_KEY, 'employees', range.from, range.to],
    queryFn: ({ signal }) => fetchEmployeeProgress(range.from, range.to, signal),
  })
  const departments = useQuery({
    queryKey: [...PLANS_KEY, 'departments', range.from, range.to],
    queryFn: ({ signal }) => fetchDepartmentProgress(range.from, range.to, signal),
  })
  const forbidden = isForbidden(employees.error)

  const switcher = (
    <div className="row" style={{ gap: 6, alignItems: 'center' }} role="group" aria-label={t('plans.quarter')}>
      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('plans.quarter.prev')}
              onClick={() => setQuarter(shiftQuarter(quarter, -1))}>
        <Icon name="arrow-left" size="sm" />
      </button>
      <span className="strong" aria-live="polite">{t('plans.quarter.label', { q: quarter.q, year: quarter.year })}</span>
      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('plans.quarter.next')}
              onClick={() => setQuarter(shiftQuarter(quarter, 1))}>
        <Icon name="arrow-right" size="sm" />
      </button>
    </div>
  )

  const employeeRows = employees.data ?? []
  const departmentRows = departments.data ?? []

  return (
    <AdminPage
      sectionKey="nav.section.sales"
      titleKey="nav.salesPlans"
      leadKey="plans.lead"
      forbidden={forbidden}
      headExtra={switcher}
      primary={mayCreate && <PrimaryButton label={t('plans.add')} onClick={() => setCreating(true)} />}
    >
      <section className="stack" style={{ gap: 10 }} aria-labelledby="plans-employees-h">
        <h2 id="plans-employees-h" className="h2">{t('plans.employees')}</h2>
        <AdminState
          isLoading={employees.isLoading}
          error={employees.error}
          onRetry={() => void employees.refetch()}
          forbiddenTitleKey="plans.forbidden"
          errorTitleKey="plans.error"
          skeleton={<SkeletonTable columns={6} rows={4} />}
        >
          {employeeRows.length === 0 ? (
            <StateView boxed kind="empty" title={t('plans.employees.empty')} description={t('plans.employees.empty.text')}
                       actions={mayCreate && (
                         <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>{t('plans.add')}</button>
                       )} />
          ) : (
            <ProgressTable
              label={t('plans.employees')}
              nameHeader={t('plans.col.employee')}
              rows={employeeRows.map((row) => ({ ...row, name: row.employeeName ?? t('plans.unnamed') }))}
              range={range}
              locale={locale}
              onOpen={(row) => setOpened({ kind: 'employee', planId: row.planId, name: row.name, currency: row.currency, planned: row.planned })}
              menu={(row) => mayRevise ? [{
                id: 'revise', label: t('plans.revise'), icon: 'adm-edit',
                onSelect: () => setRevising({ kind: 'employee', planId: row.planId, name: row.name, currency: row.currency, planned: row.planned }),
              }] : []}
            />
          )}
        </AdminState>
      </section>

      {!forbidden && (
        <section className="stack" style={{ gap: 10, marginTop: 24 }} aria-labelledby="plans-departments-h">
          <h2 id="plans-departments-h" className="h2">{t('plans.departments')}</h2>
          <AdminState
            isLoading={departments.isLoading}
            error={departments.error}
            onRetry={() => void departments.refetch()}
            forbiddenTitleKey="plans.forbidden"
            errorTitleKey="plans.error"
            skeleton={<SkeletonTable columns={6} rows={2} />}
          >
            {departmentRows.length === 0 ? (
              <StateView boxed kind="empty" title={t('plans.departments.empty')} />
            ) : (
              <ProgressTable
                label={t('plans.departments')}
                nameHeader={t('plans.col.department')}
                rows={departmentRows.map((row: DepartmentProgress) => ({ ...row, name: row.departmentName ?? t('plans.unnamed') }))}
                range={range}
                locale={locale}
                onOpen={(row) => setOpened({ kind: 'department', planId: row.planId, name: row.name, currency: row.currency, planned: row.planned })}
                menu={(row) => mayRevise ? [{
                  id: 'revise', label: t('plans.revise'), icon: 'adm-edit',
                  onSelect: () => setRevising({ kind: 'department', planId: row.planId, name: row.name, currency: row.currency, planned: row.planned }),
                }] : []}
              />
            )}
          </AdminState>
        </section>
      )}

      {creating && <CreatePlanSheet range={range} onClose={() => setCreating(false)} />}
      {opened && <PlanDetailSheet plan={opened} onClose={() => setOpened(null)} />}
      {revising && <RevisePlanSheet plan={revising} onClose={() => setRevising(null)} />}
    </AdminPage>
  )
}

/** План, открытый в боковой панели. */
interface Opened {
  kind: 'employee' | 'department'
  planId: string
  name: string
  currency: string
  planned: number
}

type Row = Pick<EmployeeProgress, 'planId' | 'periodStart' | 'periodEnd' | 'planned' | 'actual' | 'completionPercent' | 'currency'> & { name: string }

function ProgressTable({ label, nameHeader, rows, range, locale, onOpen, menu }: {
  label: string
  nameHeader: string
  rows: Row[]
  range: { from: string; to: string }
  locale: Locale
  onOpen: (row: Row) => void
  menu: (row: Row) => MenuAction[]
}) {
  const { t } = useApp()
  const withMenu = rows.some((row) => menu(row).length > 0)
  return (
    <div className="tbl-wrap">
      <table className="tbl" aria-label={label}>
        <thead>
          <tr>
            <th>{nameHeader}</th>
            <th>{t('plans.col.period')}</th>
            <th className="num">{t('plans.col.planned')}</th>
            <th className="num">{t('plans.col.actual')}</th>
            <th style={{ minWidth: 180 }}>{t('plans.col.completion')}</th>
            <th><span className="sr-only">{t('plans.col.detail')}</span></th>
            {withMenu && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const items = menu(row)
            return (
              <tr key={row.planId}>
                <td className="strong">{row.name}</td>
                <td>{row.periodStart === range.from && row.periodEnd === range.to
                  ? t('plans.wholeQuarter')
                  : `${formatDate(row.periodStart, locale)} — ${formatDate(row.periodEnd, locale)}`}</td>
                <td className="num">{formatMoney(row.planned, row.currency, locale)}</td>
                <td className="num">{formatMoney(row.actual, row.currency, locale)}</td>
                <td><Completion percent={row.completionPercent} locale={locale} /></td>
                <td>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(row)}
                          aria-label={t('plans.open', { name: row.name })}>
                    {t('plans.forecastAndMonths')}
                  </button>
                </td>
                {withMenu && (
                  <td className="adm-act">
                    {items.length > 0 && <RowMenu label={t('plans.actions', { name: row.name })} items={items} />}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Выполнение полосой и числом; нулевой план — прочерк со словами, а не 0%. */
export function Completion({ percent, locale }: { percent: number | undefined | null; locale: Locale }) {
  const { t } = useApp()
  if (percent == null) {
    return <span className="muted" title={t('plans.noTarget.hint')}>{t('plans.noTarget')}</span>
  }
  const met = percent >= 100
  return (
    <span className="row" style={{ gap: 8, alignItems: 'center' }}>
      <span className={met ? 'meter is-success' : 'meter'} style={{ width: 100 }} aria-hidden="true">
        <i style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
      </span>
      <span className="mono">{t('plans.percent', { value: formatNumber(percent, locale) })}</span>
    </span>
  )
}

// ------------------------------------------------------------------ подробности

function PlanDetailSheet({ plan, onClose }: { plan: Opened; onClose: () => void }) {
  const { t, locale } = useApp()
  const months = useQuery({
    queryKey: [...PLANS_KEY, 'breakdown', plan.planId],
    queryFn: ({ signal }) => fetchBreakdown(plan.planId, signal),
  })
  // The pipeline belongs to a person: the server refuses a department forecast
  // (plan.forecast_unsupported), so the screen does not ask and says why instead.
  const forecast = useQuery({
    queryKey: [...PLANS_KEY, 'forecast', plan.planId],
    queryFn: ({ signal }) => fetchForecast(plan.planId, signal),
    enabled: plan.kind === 'employee',
  })
  const money = (value: number) => formatMoney(value, plan.currency, locale)
  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('plans.detail')}
      title={plan.name}
      subtitle={t('plans.detail.planned', { amount: money(plan.planned) })}
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>{t('plans.close')}</button>}
    >
      <section className="stack" style={{ gap: 8 }} aria-label={t('plans.forecast')}>
        <span className="overline">{t('plans.forecast')}</span>
        {plan.kind !== 'employee' ? (
          <p className="muted t-sm">{t('plans.forecast.employeeOnly')}</p>
        ) : (
          <AdminState isLoading={forecast.isLoading} error={forecast.error} onRetry={() => void forecast.refetch()}
                      forbiddenTitleKey="plans.forbidden" errorTitleKey="plans.forecast.error"
                      skeleton={<SkeletonTable columns={2} rows={3} />}>
            {forecast.data && (
              <dl className="kv" aria-label={t('plans.forecast')}>
                <dt>{t('plans.forecast.actual')}</dt>
                <dd className="mono">{money(forecast.data.actual)}</dd>
                <dt>{t('plans.forecast.pipeline')}</dt>
                <dd className="mono">{money(forecast.data.weightedPipeline)}</dd>
                <dt>{t('plans.forecast.projected')}</dt>
                <dd className="mono">
                  {money(forecast.data.projected)}
                  {forecast.data.projectedPercent != null && ` · ${t('plans.percent', { value: formatNumber(forecast.data.projectedPercent, locale) })}`}
                </dd>
                <dt>{t('plans.forecast.verdict')}</dt>
                <dd>
                  <span className={forecast.data.onTrack ? 'badge b-success' : 'badge b-warning'}>
                    {forecast.data.onTrack ? t('plans.forecast.onTrack') : t('plans.forecast.behind')}
                  </span>
                </dd>
              </dl>
            )}
          </AdminState>
        )}
      </section>
      <section className="stack" style={{ gap: 8, marginTop: 16 }}>
        <span className="overline">{t('plans.months')}</span>
        <AdminState isLoading={months.isLoading} error={months.error} onRetry={() => void months.refetch()}
                    forbiddenTitleKey="plans.forbidden" errorTitleKey="plans.months.error"
                    skeleton={<SkeletonTable columns={5} rows={3} />}>
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('plans.months')}>
              <thead>
                <tr>
                  <th>{t('plans.col.month')}</th>
                  <th className="num">{t('plans.col.planned')}</th>
                  <th className="num">{t('plans.col.actual')}</th>
                  <th className="num">{t('plans.col.cumulative')}</th>
                  <th>{t('plans.col.completion')}</th>
                </tr>
              </thead>
              <tbody>
                {(months.data ?? []).map((slice) => (
                  <tr key={slice.month}>
                    <td>{monthLabel(slice.month, locale)}</td>
                    <td className="num">{money(slice.planned)}</td>
                    <td className="num">{slice.actual == null ? '—' : money(slice.actual)}</td>
                    <td className="num">{money(slice.cumulativeActual)}</td>
                    <td><Completion percent={slice.completionPercent} locale={locale} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminState>
      </section>
    </EditLayer>
  )
}

function monthLabel(month: string, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) return month
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    .format(new Date(Number(match[1]), Number(match[2]) - 1, 1))
}

// ------------------------------------------------------------------ заведение

function CreatePlanSheet({ range, onClose }: { range: { from: string; to: string }; onClose: () => void }) {
  const { t } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const assignees = useQuery({ queryKey: [...PLANS_KEY, 'assignees'], queryFn: ({ signal }) => fetchAssignees(signal) })
  const [kind, setKind] = useState<'employee' | 'department'>('employee')
  const [subject, setSubject] = useState('')
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [amountText, setAmountText] = useState('')
  const [currency, setCurrency] = useState('RUB')
  const [error, setError] = useState<string | null>(null)

  const amount = parseAmount(amountText)
  const amountError = amountText.trim() !== '' && amount == null ? t('plans.form.amountInvalid') : null
  const periodError = from !== '' && to !== '' && to < from ? t('plans.form.periodInvalid') : null
  const currencyError = /^[A-Za-z]{3}$/.test(currency.trim()) ? null : t('plans.form.currencyInvalid')
  const incomplete = subject === '' || amount == null || from === '' || to === ''
    || periodError != null || currencyError != null

  const save = useMutation({
    mutationFn: (input: PlanInput) => createPlan(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLANS_KEY })
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  const people = assignees.data?.employees ?? []
  const units = assignees.data?.departments ?? []

  return (
    <EditLayer
      kind="sheet"
      overline={t('plans.form')}
      title={t('plans.new')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('plans.add')} pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <AdminState isLoading={assignees.isLoading} error={assignees.error} onRetry={() => void assignees.refetch()}
                  forbiddenTitleKey="plans.form.forbidden" errorTitleKey="plans.error"
                  skeleton={<SkeletonTable columns={2} rows={4} />}>
        <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('plans.form')} onSubmit={(event) => {
          event.preventDefault()
          if (incomplete || amount == null) return
          setError(null)
          const input: PlanInput = {
            ...(kind === 'employee' ? { employeeId: subject } : { departmentId: subject }),
            periodStart: from,
            periodEnd: to,
            amount,
            currency: currency.trim().toUpperCase(),
          }
          save.mutate(input)
        }}>
          {units.length > 0 && (
            <Field label={t('plans.form.kind')}>
              <div className="row" style={{ gap: 12 }} role="radiogroup" aria-label={t('plans.form.kind')}>
                <label className="row" style={{ gap: 6 }}>
                  <input type="radio" name="plan-kind" checked={kind === 'employee'}
                         onChange={() => { setKind('employee'); setSubject('') }} />
                  {t('plans.form.kind.employee')}
                </label>
                <label className="row" style={{ gap: 6 }}>
                  <input type="radio" name="plan-kind" checked={kind === 'department'}
                         onChange={() => { setKind('department'); setSubject('') }} />
                  {t('plans.form.kind.department')}
                </label>
              </div>
            </Field>
          )}
          {kind === 'employee' ? (
            <Field label={t('plans.col.employee')} htmlFor="plan-employee" required
                   help={people.length === 0 ? t('plans.form.nobody') : t('plans.form.employee.help')}>
              <select id="plan-employee" className="input" value={subject} onChange={(e) => setSubject(e.target.value)}>
                <option value="">{t('plans.form.choose')}</option>
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.departmentName ? `${person.name} · ${person.departmentName}` : person.name}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label={t('plans.col.department')} htmlFor="plan-department" required>
              <select id="plan-department" className="input" value={subject} onChange={(e) => setSubject(e.target.value)}>
                <option value="">{t('plans.form.choose')}</option>
                {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
              </select>
            </Field>
          )}
          <div className="form-grid">
            <Field label={t('plans.form.from')} htmlFor="plan-from" required help={t('plans.form.period.help')}>
              <input id="plan-from" type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('plans.form.to')} htmlFor="plan-to" required error={periodError}>
              <input id="plan-to" type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
          <div className="form-grid">
            <Field label={t('plans.form.amount')} htmlFor="plan-amount" required error={amountError}>
              <input id="plan-amount" className="input mono" inputMode="decimal" value={amountText}
                     onChange={(e) => setAmountText(e.target.value)} />
            </Field>
            <Field label={t('plans.form.currency')} htmlFor="plan-currency" required error={currencyError}>
              <input id="plan-currency" className="input mono" maxLength={3} value={currency}
                     onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </Field>
          </div>
          <Notice text={error} />
        </form>
      </AdminState>
    </EditLayer>
  )
}

// ------------------------------------------------------------------ пересмотр

function RevisePlanSheet({ plan, onClose }: { plan: Opened; onClose: () => void }) {
  const { t, locale } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [amountText, setAmountText] = useState(String(plan.planned))
  const [error, setError] = useState<string | null>(null)
  const amount = parseAmount(amountText)
  const save = useMutation({
    mutationFn: (value: number) => revisePlan(plan.planId, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PLANS_KEY })
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="modal"
      large={false}
      title={t('plans.revise.title', { name: plan.name })}
      subtitle={t('plans.detail.planned', { amount: formatMoney(plan.planned, plan.currency, locale) })}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} disabled={amount == null} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} onSubmit={(event) => {
        event.preventDefault()
        if (amount == null) return
        setError(null)
        save.mutate(amount)
      }}>
        <Field label={t('plans.form.amount')} htmlFor="plan-revise" required help={t('plans.revise.help')}
               error={amountText.trim() !== '' && amount == null ? t('plans.form.amountInvalid') : null}>
          <input id="plan-revise" className="input mono" inputMode="decimal" value={amountText}
                 onChange={(e) => setAmountText(e.target.value)} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
